
import type Vue from 'vue'
import type { Route } from 'vue-router'
import type { FeatureNode } from '../models'
import { PermissionLevelEnum } from '@empathybroker/tsclient-play/dist/enums/permission-level.enum'

import { computed, defineComponent, getCurrentInstance } from 'vue'
import { mapActions, mapGetters } from 'vuex'
import BreadcrumbList from '../components/breadcrumb-list.component.vue'
import FeedbackModal from '../components/feedback/feedback-modal.component.vue'
import Filters from '../components/filters/filters.component.vue'
import LoadingMask from '../components/loading-mask.component.vue'
import MobileAlertModal from '../components/mobile/mobile-alert-modal.component.vue'
import NavigationMenu from '../components/navigation-menu/navigation-menu.component.vue'
import { EpbButton } from '../components/shared/epb-button'
import TransitionSize from '../components/transition-size.component.vue'
import { useAppLoading } from '../composables/use-app-loading'

import { DeviceEnum, FeatureIdEnum, KeyboardCodeEnum } from '../enums'

import {
  AuthorizeRouteGuard,
  GUARDS_EXECUTOR,
  SetContextWithUrlGuard,
  SetFiltersVisibilityGuard,
  SetFullscreenFromUrlGuard,
  TaggingGuard,
  ToolingGuard,
} from '../guards'
import { APP_CONSTS } from '../store/app/app.consts'
import { AUTH_CONSTS } from '../store/auth/auth.consts'
import { COMMON_CONSTS } from '../store/common/common.consts'
import { CONFIG_CONSTS } from '../store/config/config.consts'
import { CONTEXT_CONSTS } from '../store/context/context.consts'
import { TAGGING_CONSTS } from '../store/tagging/tagging.consts'

import { FeaturesTreeUtils } from '../utils'
import Instances from './instances.view.vue'

export default defineComponent({
  name: 'Home',
  components: {
    BreadcrumbList,
    EpbButton,
    FeedbackModal,
    Filters,
    Instances,
    MobileAlertModal,
    NavigationMenu,
    TransitionSize,
    LoadingMask,
  },
  beforeRouteEnter: GUARDS_EXECUTOR([
    AuthorizeRouteGuard.execute,
    SetContextWithUrlGuard.execute,
    SetFullscreenFromUrlGuard.execute,
    SetFiltersVisibilityGuard.execute,
    ToolingGuard.execute,
    TaggingGuard.execute,
  ]),
  beforeRouteUpdate: GUARDS_EXECUTOR([
    AuthorizeRouteGuard.execute,
    SetContextWithUrlGuard.execute,
    SetFullscreenFromUrlGuard.execute,
    SetFiltersVisibilityGuard.execute,
    ToolingGuard.execute,
    TaggingGuard.execute,
  ]),
  setup() {
    const {
      proxy: { $epb },
    } = getCurrentInstance() as { proxy: { $epb: Vue['$epb'] } }
    const { loading } = useAppLoading()

    const isSmallDevice = computed(() => $epb.device <= DeviceEnum.SMALL_TABLET)

    return {
      loading,
      isSmallDevice,
    }
  },
  data() {
    return {
      menuOpened: false,
      filtersOpened: false,
      messages: this.$epb.messages.app,
      FeatureIdEnum,
      DeviceEnum,
    }
  },
  computed: {
    ...mapGetters(APP_CONSTS.moduleName, {
      fullscreen: APP_CONSTS.getters.fullscreen,
      menuCollapsed: APP_CONSTS.getters.menuCollapsed,
      filtersHidden: APP_CONSTS.getters.filtersHidden,
      featuresTree: APP_CONSTS.getters.featuresTree,
    }),
    ...mapGetters(AUTH_CONSTS.moduleName, {
      isLoggedIn: AUTH_CONSTS.getters.isLoggedIn,
    }),
    ...mapGetters(CONFIG_CONSTS.moduleName, {
      instances: CONFIG_CONSTS.getters.instances,
      currentInstance: CONFIG_CONSTS.getters.currentInstance,
    }),
    ...mapGetters(CONTEXT_CONSTS.moduleName, {
      context: CONTEXT_CONSTS.getters.context,
      enabledFilters: CONTEXT_CONSTS.getters.enabledFilters,
      appliedFiltersCount: CONTEXT_CONSTS.getters.appliedFiltersCount,
    }),
    classes(): (string | Record<string, boolean>)[] {
      return [
        'home__content-wrapper',
        `home__content-wrapper--${this.$route.meta.featureId}`,
        {
          'home__content-wrapper--menu-collapsed': !!this.menuCollapsed,
        },
      ]
    },
    title(): string {
      const routeMeta = this.$route.meta
      return (
        this.getDomainMessage(this.messages.routes[routeMeta.titleI18nKey]) ||
        this.getDomainMessage(this.messages.features[routeMeta.featureId])
      )
    },
    filters() {
      return Object.values(this.enabledFilters)
    },
    areFiltersVisible(): boolean {
      return !this.filtersHidden && !!this.filters.length
    },
    removeContentPadding() {
      return this.$route.meta?.style ? this.$route.meta.style.removeContentPadding : false
    },
    backgroundContrast(): boolean {
      return this.$route.meta?.style ? this.$route.meta.style.backgroundContrast : false
    },
  },
  beforeDestroy(): void {
    document.removeEventListener('keyup', this.escapeKeyListener)
  },
  mounted(): void {
    document.addEventListener('keyup', this.escapeKeyListener)
  },
  methods: {
    ...mapActions({
      setCurrentInstance: COMMON_CONSTS.actions.setCurrentInstance,
    }),
    ...mapActions(TAGGING_CONSTS.moduleName, {
      tagAction: TAGGING_CONSTS.actions.tagAction,
    }),
    getDomainMessage(message: string | object): string {
      return message instanceof Object ? message[this.context.domain] : message
    },
    toggleMenu() {
      this.menuOpened = !this.menuOpened
    },
    toggleFilters() {
      this.filtersOpened = !this.filtersOpened
    },
    closeMenuOrFilters() {
      if (this.menuOpened) {
        this.toggleMenu()
      } else if (this.filtersOpened) {
        this.toggleFilters()
      }
    },
    onContextChanged(route: Route = this.$route): void {
      this.$router.replace(SetContextWithUrlGuard.updateRouteWithContext(route, this.context))
    },
    resetScroll(): void {
      this.$refs.content.scrollTop = 0
    },
    escapeKeyListener(keyboardEvent: KeyboardEvent) {
      if (keyboardEvent.key === KeyboardCodeEnum.ESC && (this.menuOpened || this.filtersOpened)) {
        this.menuOpened = false
        this.filtersOpened = false
      }
    },
    hasFeaturePermission(featureId: FeatureIdEnum, featureNodes: FeatureNode[]) {
      const node = FeaturesTreeUtils.getFeatureNode(featureId, featureNodes)
      return !!node && node.permissionLevel !== PermissionLevelEnum.NONE
    },
  },
})
